import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { AuthScreen } from "./components/AuthScreen";
import Assistant from "./pages/Assistant";
import Dashboard from "./pages/Dashboard";
import Memory from "./pages/Memory";
import Account from "./pages/Account";
import Admin from "./pages/Admin";
import Vision from "./pages/Vision";
import Trading from "./pages/Trading";
import Football from "./pages/Football";
import TvMode from "./pages/TvMode";
import PaymentResult from "./pages/PaymentResult";
import Landing from "./pages/Landing";
import { VoiceFallbackBanner } from "./components/VoiceFallbackBanner";
import { VoiceProvider } from "./context/VoiceContext";
import { FloatingVoiceOrb } from "./components/FloatingVoiceOrb";
import { PremiumRequired } from "./components/PremiumRequired";
import { PWAInstallPrompt } from "./components/PWAInstallPrompt";
import { hasPremium } from "./lib/plan";

function Shell() {
  const { user } = useAuth();
  const isAuthed = user && typeof user === "object";
  const authing = user === null; // still verifying the session — don't redirect yet
  // Protected route guard: wait while the session is being checked so a hard
  // reload / deep-link on a private page (e.g. /compte) doesn't bounce to /auth.
  const guard = (el) => (isAuthed ? el : authing ? <div data-testid="route-checking" /> : <Navigate to="/auth" replace />);
  // Premium guard: Trading, Vision and Studio are reserved for Pro & Enterprise
  // (and admins). Free users see an upsell screen instead of the feature.
  const premiumGuard = (el, feature) =>
    isAuthed
      ? hasPremium(user)
        ? el
        : <PremiumRequired feature={feature} />
      : authing
      ? <div data-testid="route-checking" />
      : <Navigate to="/auth" replace />;

  return (
    <div className="App">
      <div className="space-bg" />
      <div className="scanlines" />
      <div className="grain" />

      <div className="relative z-10">
        {user === null && (
          <div className="fixed inset-0 flex items-center justify-center pointer-events-none z-[70]">
            <span className="font-mono-hud text-xs tracking-[0.35em] accent-text animate-pulse">
              CONNEXION EN COURS...
            </span>
          </div>
        )}
        <Routes>
          {/* Public */}
          <Route path="/" element={isAuthed ? <Assistant /> : authing ? <div data-testid="route-checking" /> : <Landing />} />
          <Route path="/auth" element={isAuthed ? <Navigate to="/" replace /> : authing ? <div data-testid="route-checking" /> : <AuthScreen />} />
          <Route path="/payment/success" element={<PaymentResult mode="success" />} />
          <Route path="/payment/cancel" element={<PaymentResult mode="cancel" />} />

          {/* Authed */}
          <Route path="/tableau-de-bord" element={guard(<Dashboard />)} />
          <Route path="/memoire" element={guard(<Memory />)} />
          <Route path="/compte" element={guard(<Account />)} />
          <Route path="/admin" element={guard(<Admin />)} />
          <Route path="/vision" element={premiumGuard(<Vision />, "vision")} />
          <Route path="/trading" element={premiumGuard(<Trading />, "trading")} />
          <Route path="/football" element={premiumGuard(<Football />, "football")} />
          <Route path="/tv" element={guard(<TvMode />)} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>
      {isAuthed && <VoiceFallbackBanner />}
      {isAuthed && <FloatingVoiceOrb />}
      <PWAInstallPrompt />
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <VoiceProvider>
          <Shell />
        </VoiceProvider>
        <Toaster position="top-right" theme="dark" />
      </AuthProvider>
    </BrowserRouter>
  );
}
